import { useState } from 'react';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { cn } from '../../../lib/utils';
import { useAppMode } from '../../../contexts/AppModeContext';
import {
  Users, Star, Clock, Shield, MapPin, Calendar, Phone, Mail,
  Sparkles, TrendingUp, AlertTriangle, CheckCircle2, ArrowRight,
  Settings, Copy, Plus, BarChart3, DollarSign,
} from 'lucide-react';
import { toast } from 'sonner';

/* ═══ Real-mode empty placeholder (prevents demo literals from leaking) ═══ */
const RealModeEmpty = ({ testId, icon: Icon, title, subtitle }) => (
  <Card className="border-dashed border-slate-200" data-testid={testId}>
    <CardContent className="p-10 text-center">
      <Icon className="h-8 w-8 text-slate-300 mx-auto mb-2" />
      <p className="text-sm font-medium text-slate-600">{title}</p>
      <p className="text-[11px] text-slate-400 mt-1">{subtitle}</p>
    </CardContent>
  </Card>
);

/* ═══ WAITLIST TAB — Edits 4 & 5 live in ./WaitlistTab.js ═══ */
export { WaitlistTab } from './WaitlistTab';

/* ═══ TEMPLATES TAB ═══ */
const TEMPLATES = [
  { id: 't1', provider: 'Dr. Chen', specialty: 'Internal Medicine', days: 'Mon–Fri', hours: '8:00 AM – 5:00 PM', maxNew: 2, maxFollowup: 8, sickSlots: 2, lunchBlock: '12:00 – 1:00 PM', holdSlots: 1 },
  { id: 't2', provider: 'Dr. Wilson', specialty: 'Family Medicine', days: 'Mon–Sat', hours: '8:30 AM – 4:30 PM', maxNew: 3, maxFollowup: 6, sickSlots: 3, lunchBlock: '12:30 – 1:30 PM', holdSlots: 2 },
];

export const TemplatesTab = () => {
  const { isDemo } = useAppMode();
  if (!isDemo) {
    return <RealModeEmpty testId="templates-tab-empty-real" icon={Calendar} title="Schedule templates are empty in real mode" subtitle="Templates will load from the scheduler API once configured." />;
  }
  return (
  <div className="space-y-4" data-testid="templates-tab">
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <Calendar className="h-4 w-4 text-slate-500" />
        <p className="text-sm font-semibold text-slate-800">Schedule Templates</p>
      </div>
      <Button variant="outline" size="sm" className="h-8 text-[10px] gap-1" data-testid="add-template-btn" onClick={() => toast.info('Template editor coming soon')}>
        <Plus className="h-3 w-3" /> Add Template
      </Button>
    </div>

    {TEMPLATES.map(t => (
      <Card key={t.id} className="border-slate-200" data-testid={`template-card-${t.id}`}>
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-sm font-semibold text-slate-800">{t.provider}</p>
              <p className="text-[10px] text-slate-400">{t.specialty}</p>
            </div>
            <div className="flex gap-1.5">
              <Button variant="outline" size="sm" className="h-7 text-[10px] gap-1"><Copy className="h-2.5 w-2.5" />Duplicate</Button>
              <Button variant="outline" size="sm" className="h-7 text-[10px] gap-1"><Settings className="h-2.5 w-2.5" />Edit</Button>
            </div>
          </div>
          <div className="grid grid-cols-4 gap-3 text-[10px]">
            <div className="p-2.5 rounded-lg bg-slate-50">
              <p className="text-slate-400 uppercase text-[9px] font-semibold">Operating hours</p>
              <p className="text-slate-700 font-medium mt-0.5">{t.days}</p>
              <p className="text-slate-500">{t.hours}</p>
            </div>
            <div className="p-2.5 rounded-lg bg-slate-50">
              <p className="text-slate-400 uppercase text-[9px] font-semibold">Daily capacity</p>
              <p className="text-slate-700 font-medium mt-0.5">{t.maxNew} new · {t.maxFollowup} follow-up</p>
              <p className="text-slate-500">{t.sickSlots} sick slots</p>
            </div>
            <div className="p-2.5 rounded-lg bg-slate-50">
              <p className="text-slate-400 uppercase text-[9px] font-semibold">Blocks</p>
              <p className="text-slate-700 font-medium mt-0.5">Lunch: {t.lunchBlock}</p>
              <p className="text-slate-500">Admin: Last 30min</p>
            </div>
            <div className="p-2.5 rounded-lg bg-slate-50">
              <p className="text-slate-400 uppercase text-[9px] font-semibold">Hold slots</p>
              <p className="text-slate-700 font-medium mt-0.5">{t.holdSlots} same-day urgent</p>
              <p className="text-slate-500">Double-book: 15min follow-ups only</p>
            </div>
          </div>
        </CardContent>
      </Card>
    ))}
  </div>
  );
};

/* ═══ ANALYTICS TAB ═══ */
const METRICS = [
  { label: 'Slot utilization', value: '91%', target: '≥85%', status: 'good', benchmark: 'Echo avg: 91%' },
  { label: 'No-show rate', value: '8.2%', target: '<10%', status: 'good', benchmark: 'Industry: 19%' },
  { label: 'Copay collection rate', value: '94%', target: '≥95%', status: 'warning', benchmark: 'Best: 96%' },
  { label: 'Avg time to fill cancelled slot', value: '12 min', target: '<30 min', status: 'good', benchmark: 'Echo AI: 8 min' },
  { label: 'Self-check-in rate', value: '76%', target: '≥80%', status: 'warning', benchmark: '—' },
  { label: 'Care gap outreach conversion', value: '32%', target: '—', status: 'good', benchmark: 'Tracked' },
];

export const AnalyticsTab = () => {
  const { isDemo } = useAppMode();
  if (!isDemo) {
    return <RealModeEmpty testId="analytics-tab-empty-real" icon={BarChart3} title="Analytics will populate once the scheduler has live data" subtitle="Real-mode metrics are disabled until the reporting API is wired." />;
  }
  return (
  <div className="space-y-4" data-testid="analytics-tab">
    <div className="flex items-center gap-2">
      <BarChart3 className="h-4 w-4 text-slate-500" />
      <p className="text-sm font-semibold text-slate-800">Scheduler Analytics</p>
      <Badge className="text-[9px] border-0 bg-slate-100 text-slate-500">Last 30 days</Badge>
    </div>

    {/* Metrics grid */}
    <div className="grid grid-cols-3 gap-3">
      {METRICS.map((m, i) => (
        <Card key={i} className="border-slate-200" data-testid={`metric-card-${i}`}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-1">
              <p className="text-[10px] text-slate-500">{m.label}</p>
              {m.status === 'good' ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" /> : <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />}
            </div>
            <p className={cn("text-2xl font-bold font-['Plus_Jakarta_Sans']", m.status === 'good' ? 'text-emerald-600' : 'text-amber-600')}>{m.value}</p>
            <div className="flex items-center justify-between mt-1">
              <span className="text-[9px] text-slate-400">Target: {m.target}</span>
              <span className="text-[9px] text-slate-400">{m.benchmark}</span>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>

    {/* AI Weekly Performance */}
    <Card className="border-violet-200 bg-violet-50/30" data-testid="ai-performance-card">
      <CardContent className="p-5">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="h-4 w-4 text-violet-500" />
          <p className="text-sm font-semibold text-slate-800">Echo AI — your week in review</p>
        </div>
        <div className="grid grid-cols-5 gap-4">
          {[
            { label: 'No-shows prevented', value: '4', sub: 'Est. saved: $800', color: 'text-emerald-600' },
            { label: 'Calls handled', value: '23', sub: 'Staff hours freed: 3.5', color: 'text-blue-600' },
            { label: 'Slots filled (waitlist)', value: '6', sub: 'Revenue recovered: $900', color: 'text-violet-600' },
            { label: 'Copays collected', value: '$1,240', sub: '94% collection rate', color: 'text-emerald-600' },
            { label: 'Self check-ins', value: '18', sub: 'Forms auto-loaded: 18', color: 'text-blue-600' },
          ].map((s, i) => (
            <div key={i}>
              <p className="text-[10px] text-slate-500">{s.label}</p>
              <p className={cn("text-lg font-bold font-['Plus_Jakarta_Sans'] mt-0.5", s.color)}>{s.value}</p>
              <p className="text-[9px] text-slate-400 mt-0.5">{s.sub}</p>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  </div>
  );
};
